import {
  Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnDestroy, OnInit
} from '@angular/core';
import { Observable, Subscription, combineLatest, map, of, switchMap, tap } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { Params } from '@angular/router';

import {
  HeaderWidget, HeaderWidgetConfig, HeaderSupportType, HeaderWidgetType
} from '@design-system/components/templates/header/entities/header.types';
import { DsHeaderConfig, HeaderPageType } from '@header/entities/ds-header-config';
import { UserHistoryFacade } from '@user-history/user-history.facade';
import { PlaceNamePipe } from '@app/pipes/place-name.pipe';
import { ReservationType } from '@user-history/entities/user-history-item';
import { PlaceSelectors } from '@app/entities/place';
import { isNullOrUndefined } from '@app/util/util';
import { NavigationService } from '@app/services/navigation.service';

@Component({
  selector: 'app-header-reservation',
  templateUrl: './header-reservation.container.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HeaderReservationContainer implements OnInit, OnDestroy {

  @Input() dsHeaderConfig: DsHeaderConfig;

  @Output() widgetClicked: EventEmitter<HeaderWidget> = new EventEmitter<HeaderWidget>();

  private readonly placeCriteria = PlaceSelectors.City;

  private subscriptions: Subscription = new Subscription();
  private params: Params;

  constructor(
    private readonly transloco: TranslocoService,
    private readonly userHistoryFacade: UserHistoryFacade,
    private readonly placeNamePipe: PlaceNamePipe,
    private readonly navigationService: NavigationService,
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(
      this.params$.pipe(
        tap(params => {
          this.params = params;
        })
      ).subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }

  get widgets$(): Observable<HeaderWidgetConfig[]> {
    return combineLatest([
      this.userHistoryFacade.isLoadingDetailUserHistory$,
      this.userHistoryFacade.historyDetailTitle$,
      this.userHistoryFacade.historyDetailType$,
      this.userHistoryFacade.historyBookingDetail$,
      this.transloco.selectTranslate('USER_HISTORY_MODULE.DETAIL.HELP_CENTER'),
      this.transloco.selectTranslate('USER_HISTORY_MODULE.DETAIL.RESERVATION_NUMBER'),
      this.transloco.selectTranslate('HEADER_MODULE.TITLES.USER_HISTORY'),
    ]).pipe(
      switchMap(([isLoading, detailTitle, detailType, detail, supportLabel, idLabel, defaultTitle]) => {
        const detailId = this.dsHeaderConfig?.pageType === HeaderPageType.TripDetailCancellationPage ?
          this.params?.id : this.dsHeaderConfig?.pageType === HeaderPageType.LastTripDetailPage ?
            detail?.Id : this.params?.detailId;
        if (isLoading || isNullOrUndefined(detailTitle)) {
          return of({ detailId, title: defaultTitle, supportLabel, idLabel });
        } else {
          if (detailType === ReservationType.Hotel) {
            return of({ detailId, title: detailTitle, supportLabel, idLabel });
          } else {
            return this.placeNamePipe.transform(detailTitle, this.placeCriteria, detailType === ReservationType.Bus).pipe(
              switchMap(destination => this.transloco.selectTranslate('USER_HISTORY_MODULE.DETAIL.TRAVEL_TO_HEADER', {
                destination
              }).pipe(
                map((title) => ({ detailId, title, supportLabel, idLabel }))
              ))
            );
          }
        }
      }),
      map(({ detailId, title, supportLabel, idLabel }) => {
        return [
          {
            Type: HeaderWidgetType.Back,
          },
          {
            Type: HeaderWidgetType.ReservationInfo,
            ReservationInfo: {
              Title: title,
              Subtitle: idLabel && detailId ? `${idLabel}: ${detailId}` : ''
            }
          },
          {
            Type: HeaderWidgetType.Support,
            Label: supportLabel,
            SupportType: HeaderSupportType.Device
          },
        ];
      })
    );
  }

  private get params$(): Observable<Params> {
    return this.navigationService.events$.pipe(
      map(({ rootRoute }) => rootRoute?.snapshot?.paramMap?.['params']),
    );
  }


}
